import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphRequest } from '../../msalConfig';
import { getItems } from '../../api/figApi';
import { FAMILY_LISTS } from '../../api/figApi';
import { listOrphans } from '../api/b2';
import ClaimModal from '../components/ClaimModal';

export default function Claim() {
  const { instance, accounts } = useMsal();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const [orphans, setOrphans] = useState([]);
  const [claimedKeys, setClaimedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [b2Objects, spItems] = await Promise.all([
          listOrphans(),
          getToken().then(tok => getItems(tok, FAMILY_LISTS.MEDIA_ITEMS)),
        ]);
        if (cancelled) return;

        const claimedSet = new Set(
          spItems
            .map(item => {
              const loc = item.StorageLocation || '';
              return loc.replace(/^b2:\/\/[^/]+\//, '');
            })
            .filter(Boolean),
        );
        setClaimedKeys(claimedSet);
        setOrphans(b2Objects.filter(o => !claimedSet.has(o.key)));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [getToken]);

  function handleClaimed(newItem) {
    const key = (newItem.StorageLocation || '').replace(/^b2:\/\/[^/]+\//, '');
    setClaimedKeys(prev => new Set([...prev, key]));
    setOrphans(prev => prev.filter(o => o.key !== key));
    setSelected(null);
  }

  return (
    <div className="FV-shelf">
      <h1 className="FV-shelf-title">Claim Orphans</h1>

      {loading && <p className="FV-empty">Loading…</p>}
      {error && <p className="FV-claim-error">{error}</p>}

      {!loading && !error && orphans.length === 0 && (
        <p className="FV-empty">No unclaimed files in B2.</p>
      )}

      {!loading && orphans.length > 0 && (
        <ul className="FV-orphan-list">
          {orphans.map(o => (
            <li key={o.key} className="FV-orphan-item">
              <div className="FV-orphan-info">
                <span className="FV-orphan-key">{o.key}</span>
                <span className="FV-orphan-meta">
                  {o.mediaTypeGuess} &middot; {formatBytes(o.size)}
                </span>
              </div>
              <button className="FV-orphan-claim-btn" onClick={() => setSelected(o)}>
                Claim
              </button>
            </li>
          ))}
        </ul>
      )}

      {selected && (
        <ClaimModal
          orphan={selected}
          onClose={() => setSelected(null)}
          onClaimed={handleClaimed}
          getToken={getToken}
        />
      )}
    </div>
  );
}

function formatBytes(bytes) {
  if (bytes >= 1e9) return (bytes / 1e9).toFixed(1) + ' GB';
  if (bytes >= 1e6) return (bytes / 1e6).toFixed(1) + ' MB';
  return (bytes / 1e3).toFixed(0) + ' KB';
}
